import { css, cva } from '@/styled-system/css';
import { styled } from '@/styled-system/jsx';

import { Table } from '@zillow/constellation-10';

export const PROPERTY_ROW_WIDTHS = {
  FIRST_CELL: '36%',
  MIDDLE_CELL: '18%',
  LAST_CELL: '10%',
} as const;

const IMAGE_DIMENSIONS = {
  WIDTH: 100, // max-width of image
  HEIGHT: 60, // max-height of image
};

export const CELL_DIMENSIONS = {
  WIDTH: 200, // max-width of cell
  HEIGHT: 63, // min-height of cell
};

const hiddenTextStyles = {
  /* bypass the need for text to properly align */
  '&:before': {
    // eslint-disable-next-line curly-quotes/no-straight-quotes
    content: '"hidden"',
    display: 'flex',
    height: '0px',
    fontSize: '0px',
  },
} as const;

export const propertyPhotoContainerStyles = css.raw({
  display: 'flex',
  maxWidth: `${IMAGE_DIMENSIONS.WIDTH + 32}px`,
  minHeight: `${IMAGE_DIMENSIONS.HEIGHT}px`,
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '600',
  ...hiddenTextStyles,
});

export const propertyPhotoRecipe = cva({
  base: {
    width: `${IMAGE_DIMENSIONS.WIDTH}px`,
    height: `${IMAGE_DIMENSIONS.HEIGHT}px`,
    margin: '0 auto',
    borderRadius: '200',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },
  variants: {
    hasPhoto: {
      true: {
        backgroundSize: 'cover',
      },
      false: {
        backgroundSize: 'contain',
      },
    },
  },
});

export const flexContainerStyles = css.raw({
  display: 'flex',
  minHeight: `${CELL_DIMENSIONS.HEIGHT}px`,
  maxWidth: 'calc(100% - 128px)',
  alignItems: 'center',
  ...hiddenTextStyles,
});

export const propertyAddressStyles = css.raw({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  '& > *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const TableHeaderCell = styled(Table.HeaderCell, {
  base: {
    fontSize: 'body-sm',
    fontWeight: 'bold',
    backgroundColor: 'bg.softer',
    padding: '{spacing.200}',
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,

    '&:first-child': {
      paddingLeft: '{spacing.600}',
      width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
    },

    '&:last-child': {
      width: PROPERTY_ROW_WIDTHS.LAST_CELL,
    },

    md: {
      minWidth: '106px',

      '&:last-child': {
        minWidth: 'auto',
      },
    },
  },
});

export const TableCell = styled(Table.Cell, {
  base: {
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    height: '105px',

    // Target cells in the last row (within the last anchor wrapper)
    'tbody > a:last-child tr > &': {
      borderBottom: 'none',
    },

    '&:first-child': {
      paddingLeft: '600',
      width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
    },
    '&:last-child': {
      paddingRight: '600',
      width: PROPERTY_ROW_WIDTHS.LAST_CELL,
    },

    md: {
      padding: '{spacing.400} 0 {spacing.400} {spacing.400}',
      '&:last-child': {
        paddingRight: '400',
      },
    },

    lg: {
      paddingLeft: '200',
      '&:last-child': {
        paddingRight: '200',
      },
    },
  },
});

export const tagStyles = css.raw({
  display: 'flex',
  width: 'fit-content',
  textWrap: 'wrap',
});

export const cellAnchorStyles = css.raw({
  color: 'text.neutral',
  textDecoration: 'none',
  'td:hover &': {
    color: 'blue.500',
    textDecoration: 'underline',
  },
});
